import * as fabric from "fabric";
import React, { useEffect, useRef } from "react";
import { useModal } from "../../Context/ModalContext";
import { useAppDispatch, useAppSelector } from "../../hook/redux";
import store from "../../store";
import { createCanvasSignature } from "./createCanvasSignature";


const CanvasSignature = ({ canvas, color, modalOpen }: { canvas: fabric.Canvas, color: string, modalOpen: boolean }) => {
  const {selectedLayout} = useAppSelector(state => state.editor)
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);

  const { toggleShow, type } = useModal();
  const dispatch = useAppDispatch();

  const getScale = () => store.getState().editor.scale
  const scale = getScale()

  const updateCanvasSize = () => {
    if ( fabricCanvasRef.current ) {
      fabricCanvasRef.current.setWidth( (400 * scale) * 1.5 );
      fabricCanvasRef.current.setHeight( (200 * scale) * 1.5);
      fabricCanvasRef.current.renderAll();
    }
  };

  useEffect( () => {
    if (window) {
    window.addEventListener( "resize", updateCanvasSize );
    }
    return () => {
      window.removeEventListener( "resize", updateCanvasSize );
    };
  }, [] );

  useEffect(() => {
    if (canvasRef.current) {
      const fabricCanvas = new fabric.Canvas(canvasRef.current, {
        isDrawingMode: true,
      });
      fabricCanvasRef.current = fabricCanvas;
      fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(fabricCanvas);
      fabricCanvas.freeDrawingBrush.width = 2;
      fabricCanvas.freeDrawingBrush.color = color;

      fabricCanvas.clear();
      fabricCanvas.renderAll();
    }

    return () => {
      fabricCanvasRef.current?.dispose();
    };
  }, [modalOpen]);

  useEffect(() => {
    if (fabricCanvasRef.current && fabricCanvasRef.current.freeDrawingBrush) {

      fabricCanvasRef.current.freeDrawingBrush.color = color;

      const objects = fabricCanvasRef.current.getObjects();
      objects.forEach((obj) => {
        if (obj instanceof fabric.Path) {
          obj.set({ stroke: color });
        }
      });

      fabricCanvasRef.current.renderAll();
    }
  }, [color]);

  /**
   * Converts the canvas to a base64-encoded PNG image and creates a canvas signature using the `createCanvasSignature`
   * function. If the canvas is successfully converted to a base64 image, the `createCanvasSignature` function is
   * called with the base64 data and the `canvas` prop. If the `createCanvasSignature` function is successful, the
   * modal is closed using the `toggleShow` function. If there is an error during the creation of the canvas signature,
   * the error is logged to the console.
   */
  const handleConvertToBase64 = (): void => {

    if (fabricCanvasRef.current) {
      const base64 = fabricCanvasRef.current.toDataURL({
        format: 'png',
        quality: 0.8,
        multiplier: 1,
      });

      if (base64) {
        createCanvasSignature( base64, color, canvas, dispatch, selectedLayout )
        .then(() => {
          toggleShow(false, null, type);
        })

        .catch(error => {
          console.log("Erreur lors de la création de la signature", error);
        });
      }
    }
  };


    /**
     * Clears the canvas by calling the `clear()` method on the `fabricCanvasRef.current` instance.
     */
    const handleClearCanvas = (): void => {
        fabricCanvasRef.current?.clear();
    };

    return (
      <div className="mx-auto">
        <canvas ref={ canvasRef }
                width={ (400 * scale) * 1.5 }
                height={ (200 * scale) * 1.5 }
                style={ { border: "1px solid #000" } } />
        <div className="d-flex justify-content-center gap-3 p-3">
          <button className="btn btn-secondary" onClick={ handleClearCanvas }>
            Effacer
          </button>
          <button className="btn btn-primary" onClick={ handleConvertToBase64 }>
            Valider
          </button>
        </div>
      </div>
    );
};

export default CanvasSignature;